import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Table, Badge, Modal, Spinner, Row, Col } from 'react-bootstrap';
import { FiPlus, FiEdit2, FiTrash2, FiMessageSquare, FiFileText, FiPhone, FiCalendar, FiLayers, FiMoreHorizontal } from 'react-icons/fi';
import { toast } from 'react-toastify';
import caseActivityService, { CaseActivity, CreateActivityDto } from '../../services/case-activity.service';

interface CaseActivityLogProps {
  caseId: number;
}

const CaseActivityLog: React.FC<CaseActivityLogProps> = ({ caseId }) => {
  const [activities, setActivities] = useState<CaseActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingActivity, setEditingActivity] = useState<CaseActivity | null>(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState<CreateActivityDto>({
    activity_type: 'note',
    title: '',
    description: '',
    activity_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    loadActivities();
  }, [caseId]);

  const loadActivities = async () => {
    try {
      setLoading(true);
      const data = await caseActivityService.listByCase(caseId);
      setActivities(data);
    } catch (error) {
      toast.error('Error al cargar la bitácora');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (activity?: CaseActivity) => {
    if (activity) {
      setEditingActivity(activity);
      setFormData({
        activity_type: activity.activity_type,
        title: activity.title,
        description: activity.description || '',
        activity_date: activity.activity_date.split('T')[0]
      });
    } else {
      setEditingActivity(null);
      setFormData({
        activity_type: 'note',
        title: '',
        description: '',
        activity_date: new Date().toISOString().split('T')[0]
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingActivity(null);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('El título es requerido');
      return;
    }

    try {
      setSaving(true);
      if (editingActivity) {
        await caseActivityService.update(editingActivity.id, formData);
        toast.success('Actividad actualizada');
      } else {
        await caseActivityService.create(caseId, formData);
        toast.success('Actividad registrada');
      }
      handleCloseModal();
      loadActivities();
    } catch (error) {
      toast.error('Error al guardar la actividad');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Eliminar esta actividad de la bitácora?')) {
      try {
        await caseActivityService.delete(id);
        toast.success('Actividad eliminada');
        loadActivities();
      } catch (error) {
        toast.error('Error al eliminar');
      }
    }
  };

  const getActivityIcon = (type: string) => {
    const icons: Record<string, React.ReactNode> = {
      note: <FiMessageSquare />,
      action: <FiFileText />,
      document: <FiFileText />,
      communication: <FiPhone />,
      hearing: <FiCalendar />,
      stage_change: <FiLayers />,
      other: <FiMoreHorizontal />
    };
    return icons[type] || <FiMessageSquare />;
  };

  const getActivityBadge = (type: string) => {
    const variants: Record<string, string> = {
      note: 'info',
      action: 'primary',
      document: 'secondary',
      communication: 'warning',
      hearing: 'danger',
      stage_change: 'success',
      other: 'dark'
    };
    const labels: Record<string, string> = {
      note: 'Nota',
      action: 'Actuación',
      document: 'Documento',
      communication: 'Comunicación',
      hearing: 'Audiencia',
      stage_change: 'Cambio Etapa',
      other: 'Otro'
    };
    return <Badge bg={variants[type] || 'secondary'}>{labels[type] || type}</Badge>;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Card>
        <Card.Body className="text-center py-4">
          <Spinner animation="border" variant="primary" size="sm" />
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0">
            <FiMessageSquare className="me-2" />
            Bitácora de Seguimiento
          </h6>
          <Button variant="primary" size="sm" onClick={() => handleOpenModal()}>
            <FiPlus className="me-1" /> Nueva Entrada
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          {activities.length === 0 ? (
            <div className="text-center py-4 text-muted">
              No hay entradas en la bitácora
            </div>
          ) : (
            <Table hover className="mb-0">
              <thead>
                <tr>
                  <th style={{ width: '100px' }}>Fecha</th>
                  <th style={{ width: '120px' }}>Tipo</th>
                  <th>Descripción</th>
                  <th style={{ width: '150px' }}>Registrado por</th>
                  <th style={{ width: '80px' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {activities.map((activity) => (
                  <tr key={activity.id}>
                    <td className="text-nowrap">
                      <small>{formatDate(activity.activity_date)}</small>
                    </td>
                    <td>
                      {getActivityIcon(activity.activity_type)}{' '}
                      {getActivityBadge(activity.activity_type)}
                    </td>
                    <td>
                      <strong>{activity.title}</strong>
                      {activity.description && (
                        <div className="text-muted small mt-1">
                          {activity.description.length > 100 
                            ? activity.description.substring(0, 100) + '...' 
                            : activity.description}
                        </div>
                      )}
                    </td>
                    <td>
                      <small className="text-muted">{activity.created_by_name}</small>
                    </td>
                    <td>
                      {activity.activity_type !== 'stage_change' && (
                        <>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="me-1"
                            onClick={() => handleOpenModal(activity)}
                          >
                            <FiEdit2 />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(activity.id)}
                          >
                            <FiTrash2 />
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal para crear/editar actividad */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingActivity ? 'Editar Entrada' : 'Nueva Entrada en Bitácora'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Fecha *</Form.Label>
                  <Form.Control
                    type="date"
                    name="activity_date"
                    value={formData.activity_date}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Tipo de Actividad</Form.Label>
                  <Form.Select
                    name="activity_type"
                    value={formData.activity_type}
                    onChange={handleChange}
                  >
                    <option value="note">Nota</option>
                    <option value="action">Actuación Procesal</option>
                    <option value="document">Documento</option>
                    <option value="communication">Comunicación</option>
                    <option value="hearing">Audiencia</option>
                    <option value="other">Otro</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Título *</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Breve descripción de la actividad"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Descripción Detallada</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Detalles adicionales de la actividad..."
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" animation="border" /> : (editingActivity ? 'Actualizar' : 'Guardar')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default CaseActivityLog;
